/**
 * Internal portfolio data — used only by the private /portfolio page.
 *
 * Schema:
 *   id           : string  (kebab-case unique key)
 *   name         : string
 *   tagline      : string  (one-line hook shown on the card)
 *   description  : string  (longer copy shown in the detail modal)
 *   image        : string  (cover image path, relative to /public)
 *   category     : "Web" | "Mobile" | "Desktop" | "IoT" | "AI" | "Other"
 *   status       : "live" | "beta" | "development" | "archived" | "discontinued"
 *   year         : string
 *   client       : string  (Internal / company / individual)
 *   techStack    : string[]
 *   platforms    : Array<"web" | "ios" | "android" | "desktop">
 *   urls         : { production?, staging?, admin?, demo?, github?, docs? }
 *   appStores    : { ios?, android? }
 *   credentials  : Array<{ role, email, password, loginUrl?, notes? }>
 *   notes        : string  (internal-only notes, shown in the modal)
 *
 * NOTE: All credentials below are MOCKED placeholders. Replace with real
 * values when wiring the real data in. This file is bundled to the client,
 * so anything stored here is visible to anyone who gets past the access
 * gate — never put truly sensitive production credentials here.
 */

export const portfolioProjects = [
  {
    id: 'autofoto',
    name: 'AutoFoto',
    tagline: 'AI-powered car photography studio in your pocket',
    description:
      'Mobile app that transforms ordinary smartphone car photos into magazine-grade studio shots using a custom diffusion pipeline. Targets dealerships and private sellers.',
    image: '/images/portfolio/autofoto.png',
    category: 'Mobile',
    status: 'live',
    year: '2025',
    client: 'Internal',
    techStack: ['Flutter', 'Google ML Kit', 'Python', 'Stable Diffusion'],
    platforms: ['web', 'ios', 'android'],
    urls: {
      production: 'https://autofoto-e4de0.web.app/',
    },
    appStores: {
      android: 'https://play.google.com/store/apps/details?id=tech.hexapi.auto_foto',
    },
    credentials: [],
    notes: 'iOS coming soon via TestFlight. Android live on Google Play.',
  },
  {
    id: 'hexapi-delivery',
    name: 'HexaPi Delivery',
    tagline: 'Multi-vendor delivery platform — courier, vendor & customer apps',
    description:
      'Three-sided marketplace with live dispatch, route optimization, and SMS-based OTP onboarding. Powers two regional delivery operators.',
    image: '/images/portfolio/col2-row2a(1).png',
    category: 'Mobile',
    status: 'live',
    year: '2022',
    client: 'Multiple operators',
    techStack: ['Flutter', 'Firebase', 'PHP', 'MySQL', 'Google Maps'],
    platforms: ['ios', 'android'],
    urls: {},
    appStores: {
      android: 'https://play.google.com/store/apps/details?id=com.hexapi.delivery_driver',
    },
    credentials: [],
    notes: 'iOS coming soon.',
  },
  {
    id: 'smart-school',
    name: 'Smart School',
    tagline: 'End-to-end school management — students, teachers, parents',
    description:
      'Attendance, grades, fee tracking and parent messaging in one suite. Deployed to several private schools.',
    image: '/images/portfolio/col2-row2a(2).png',
    category: 'Web',
    status: 'live',
    year: '2020',
    client: 'Multiple schools',
    techStack: ['Flutter', 'Firebase', 'Google Cloud'],
    platforms: ['web', 'ios', 'android'],
    urls: {
      production: 'https://smart-school-hexapi.web.app/',
      demo: 'https://smart-school-hexapi.web.app/',
      docs: 'https://hexapi.tech/school.html',
    },
    credentials: [
      {
        role: 'Principal',
        email: 'principal@demo.school',
        password: 'Demo!Principal2025',
        loginUrl: 'https://smart-school-hexapi.web.app/#/login',
      },
      {
        role: 'Teacher',
        email: 'teacher@demo.school',
        password: 'Demo!Teacher2025',
      },
      {
        role: 'Parent',
        email: 'parent@demo.school',
        password: 'Demo!Parent2025',
      },
    ],
    notes: 'Demo tenant — data resets weekly.',
  },
  {
    id: 'ecommerce-platform',
    name: 'Custom E-Commerce Platform',
    tagline: 'Headless commerce without WooCommerce / Shopify lock-in',
    description:
      'Fully owned storefront with admin dashboard, inventory, orders, and Stripe / local PSP support. Several deployments in production.',
    image: '/images/portfolio/ecommerce.png',
    category: 'Web',
    status: 'live',
    year: '2024',
    client: 'Multiple',
    techStack: ['Next.js', 'MySQL', 'Prisma', 'Stripe'],
    platforms: ['web'],
    urls: {
      production: 'https://shop-demo.hexapi.tech',
      admin: 'https://shop-demo.hexapi.tech/admin',
      github: 'https://github.com/hexapi/ecommerce-starter',
    },
    credentials: [
      {
        role: 'Store Owner',
        email: 'owner@hexapi.tech',
        password: 'Demo!Shop2025',
        loginUrl: 'https://shop-demo.hexapi.tech/admin',
        notes: 'Includes Stripe test mode',
      },
    ],
    notes: 'White-label codebase — each client gets a forked instance.',
  },
  {
    id: 'portfoliocraft',
    name: 'PortfolioCraft CMS',
    tagline: 'Drop-in CMS for company portfolio sites — friendlier than WordPress',
    description:
      'Next.js front-end with Directus admin, designed for small agencies and freelancers to ship a portfolio site in under an hour.',
    image: '/images/portfolio/cms.jpg',
    category: 'Web',
    status: 'development',
    year: '2025',
    client: 'Internal',
    techStack: ['Next.js', 'Directus', 'MySQL'],
    platforms: ['web'],
    urls: {
      production: 'https://portfoliocraft.hexapi.tech',
      admin: 'https://portfoliocraft.hexapi.tech/admin',
      github: 'https://github.com/hexapi/portfoliocraft',
    },
    credentials: [
      {
        role: 'Admin',
        email: 'admin@hexapi.tech',
        password: 'Demo!Craft2025',
        loginUrl: 'https://portfoliocraft.hexapi.tech/admin',
      },
    ],
    notes: 'Pre-release. Schema migrations still in flux.',
  },
  {
    id: 'nssf-booking',
    name: 'NSSF Appointment Booking',
    tagline: 'Government-facing appointment booking with queue management',
    description:
      'Self-service booking flow with SMS reminders. Deployed for a public agency to reduce in-office wait times.',
    image: '/images/portfolio/col2-row2a(3).png',
    category: 'Mobile',
    status: 'live',
    year: '2020',
    client: 'NSSF',
    techStack: ['Flutter', 'PHP', 'Google Cloud'],
    platforms: ['ios', 'android', 'web'],
    urls: {
      production: 'https://hexapi.tech/booking.html',
      docs: 'https://hexapi.tech/booking.html',
    },
    credentials: [
      {
        role: 'Public Demo',
        email: 'demo@hexapi.tech',
        password: 'Demo!Booking2025',
      },
    ],
    notes: 'Live operational deployment. Demo account only — do not use real PII.',
  },
  {
    id: 'hexapi-auction',
    name: 'HexaPi Auction',
    tagline: 'Real-time auction platform with live video streaming',
    description:
      'WebRTC-driven live auctions with synchronized bidding state across hundreds of concurrent viewers.',
    image: '/images/portfolio/col3-row3.png',
    category: 'Web',
    status: 'archived',
    year: '2021',
    client: 'Internal',
    techStack: ['Node.js', 'WebRTC', 'Socket.io'],
    platforms: ['web'],
    urls: {
      docs: 'https://hexapi.tech/auction.html',
    },
    credentials: [],
    notes: 'Archived but recoverable — infra spun down in 2023.',
  },
  {
    id: 'tabiby',
    name: 'Tabiby',
    tagline: 'Doctor discovery & telehealth booking',
    description:
      'Patient-facing app to discover doctors, book appointments, and run video consultations.',
    image: '/images/portfolio/tabiby.png',
    category: 'Web',
    status: 'discontinued',
    year: '2024',
    client: 'Internal',
    techStack: ['React', 'MUI', 'MySQL'],
    platforms: ['web'],
    urls: {},
    credentials: [],
    notes: 'Discontinued — market validation failed. Codebase preserved.',
  },
  {
    id: 'ai-curriculum',
    name: 'AI & Tech Curriculum',
    tagline: 'K-12 AI literacy program with hands-on labs',
    description: 'Modular curriculum + classroom kits for AI literacy. Used by partner schools.',
    image: '/images/portfolio/ai lab.png',
    category: 'Other',
    status: 'live',
    year: '2024',
    client: 'Schools',
    techStack: [],
    platforms: [],
    urls: {
      docs: '/blog/ai-edu-tech-curriculum',
    },
    credentials: [],
    notes: 'See blog post for partner onboarding.',
  },
  {
    id: 'safe-plug',
    name: 'Safe Plug',
    tagline: 'Hardware invention — safer household power outlet',
    description: 'Patented safety plug design. Featured on national TV.',
    image: '/images/portfolio/Safe Plug Portfolio(1).png',
    category: 'IoT',
    status: 'discontinued',
    year: '2019',
    client: 'Internal',
    techStack: [],
    platforms: [],
    urls: {
      demo: 'https://youtu.be/3BU2JRAVFCM',
    },
    credentials: [],
    notes: 'Hardware product — see demo video.',
  },
  {
    id: 'hexapi-club-admin',
    name: 'HexaPi Club Administration Dashboard',
    tagline: 'Internal back-office for managing HexaPi Club members & activities',
    description:
      'Membership directory, event scheduling, attendance tracking, and announcements for the HexaPi Club community program.',
    image: '/images/portfolio/cms.jpg',
    category: 'Web',
    status: 'live',
    year: '2025',
    client: 'Internal',
    techStack: ['Next.js', 'MySQL', 'Tailwind CSS'],
    platforms: ['web'],
    urls: {},
    credentials: [],
    notes: 'TBD — fill in production URL, admin URL, and demo credentials.',
  },
  {
    id: 'hexapi-lms',
    name: 'HexaPi LMS',
    tagline: 'Learning Management System for courses, cohorts & assessments',
    description:
      'Multi-tenant LMS with course authoring, student progress tracking, quizzes, and certificate issuance.',
    image: '/images/portfolio/ai lab.png',
    category: 'Web',
    status: 'live',
    year: '2025',
    client: 'Internal',
    techStack: ['Next.js', 'Node.js', 'MySQL'],
    platforms: ['web'],
    urls: {},
    credentials: [],
    notes: 'TBD — fill in production URL and demo credentials.',
  },
  {
    id: 'school-management-systems',
    name: 'School Management Systems',
    tagline: 'Bespoke school management deployments — custom per-institution',
    description:
      'Tailored school management implementations built per-client requirements. Covers admissions, grading, payroll, and communications.',
    image: '/images/portfolio/summer_internship.png',
    category: 'Web',
    status: 'live',
    year: '2023',
    client: 'Multiple schools',
    techStack: ['PHP', 'Laravel', 'MySQL', 'jQuery'],
    platforms: ['web'],
    urls: {
      production: 'https://hexapi.tech/school-admin/login.php',
    },
    credentials: [
      {
        role: 'Demo',
        email: 'school',
        password: 'hexapi',
        loginUrl: 'https://hexapi.tech/school-admin/login.php',
      },
    ],
    notes: 'Custom builds. Each deployment has its own tenant — list in internal docs.',
  },
  {
    id: 'woocommerce-flutter-apps',
    name: 'WooCommerce Flutter Apps',
    tagline: 'Native mobile storefronts powered by a WordPress / WooCommerce backend',
    description:
      'Branded Flutter apps connected to WooCommerce stores via the WP REST API. Customer browsing, cart, checkout, and order tracking.',
    image: '/images/portfolio/WhatsApp Image 2023-06-13 at 3.51.43 PM.jpeg',
    category: 'Mobile',
    status: 'live',
    year: '2023',
    client: 'Multiple',
    techStack: ['Flutter', 'WordPress', 'WooCommerce', 'WP REST API'],
    platforms: ['ios', 'android'],
    urls: {},
    appStores: {},
    credentials: [],
    notes: 'Multiple client deployments — list store URLs in internal docs.',
  },
  {
    id: 'rfid-asset-tracking',
    name: 'RFID Asset Tracking System',
    tagline: 'End-to-end RFID inventory & asset tracking — hardware + software',
    description:
      'RFID readers and tag infrastructure paired with a web dashboard for live asset location, check-in/out, and audit trails.',
    image: '/images/portfolio/My project.png',
    category: 'IoT',
    status: 'live',
    year: '2024',
    client: 'Internal',
    techStack: ['ESP32', 'RFID', 'Node.js', 'MQTT', 'React'],
    platforms: ['web', 'desktop'],
    urls: {},
    credentials: [],
    notes: 'TBD — fill in production URL and hardware specs.',
  },
  {
    id: 'elsamanews-wordpress',
    name: 'ElsamaNews WordPress',
    tagline: 'News & media website built on WordPress',
    description:
      'Custom WordPress news platform with editorial workflow, category landing pages, and SEO-optimized article templates.',
    image: '/images/portfolio/IMG_20220511_141521_770.jpg',
    category: 'Web',
    status: 'live',
    year: '2023',
    client: 'ElsamaNews',
    techStack: ['WordPress', 'PHP', 'MySQL'],
    platforms: ['web'],
    urls: {},
    credentials: [],
    notes: 'TBD — fill in production URL and admin credentials.',
  },
  {
    id: 'scentimental-shopify',
    name: 'Scentimental Shopify',
    tagline: 'Shopify storefront for a fragrance brand',
    description:
      'Custom Shopify theme and storefront for Scentimental — branded product pages, checkout flow, and email automation.',
    image: '/images/portfolio/cms.jpg',
    category: 'Web',
    status: 'live',
    year: '2024',
    client: 'Scentimental',
    techStack: ['Shopify', 'Liquid', 'JavaScript'],
    platforms: ['web'],
    urls: {},
    credentials: [],
    notes: 'TBD — fill in production URL.',
  },
  {
    id: 'scalplogical',
    name: 'ScalpLogical',
    tagline: 'Website + digital book publishing platform',
    description:
      'Brand website paired with a digital book publishing & distribution workflow. Covers content authoring, paywall, and reader UX.',
    image: '/images/portfolio/about-image.png',
    category: 'Web',
    status: 'live',
    year: '2024',
    client: 'ScalpLogical',
    techStack: ['Next.js', 'MySQL', 'Stripe'],
    platforms: ['web'],
    urls: {},
    credentials: [],
    notes: 'TBD — fill in production URL and admin credentials.',
  },
];

export const projectStatusMeta = {
  live: { label: 'Live', color: 'emerald', pulse: true },
  beta: { label: 'Beta', color: 'amber', pulse: true },
  development: { label: 'In Dev', color: 'sky', pulse: false },
  archived: { label: 'Archived', color: 'slate', pulse: false },
  discontinued: { label: 'Discontinued', color: 'rose', pulse: false },
};

export const projectCategories = ['All', 'Web', 'Mobile', 'Desktop', 'IoT', 'AI', 'Other'];

export const projectPlatforms = ['web', 'ios', 'android', 'desktop'];
